import type { SupabaseClient } from '@supabase/supabase-js';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { mapWithConcurrency } from '../../../utils/concurrency';
import type { AttachmentPage, PdfAttachmentBlockData } from '../config';

/**
 * Attached PDFs: read, rasterise and upload.
 *
 * pdf.js is loaded on demand, only when a PDF is actually picked (the same way
 * heic-to is in imageInput.ts): it is a large library and an ordinary report
 * never needs it. The legacy build is used because the client is Create React
 * App 5 with TypeScript 4.9, which cannot consume the modern build's syntax
 * and types. Its worker is served from public/pdfjs/ -- the file is COPIED
 * from node_modules/pdfjs-dist/legacy/build/ and must be copied again when
 * the pinned version changes (package.json pins pdfjs-dist exactly for this
 * reason).
 *
 * Page thumbnails are rendered ONCE here, at upload, and stored beside the
 * PDF. The editor then shows plain <img>s and never runs pdf.js on a page, and
 * the export never needs the thumbnails at all: it merges the PDF's own pages
 * (see mergeAttachments in DocumentEditor).
 */

const WORKER_SRC = `${process.env.PUBLIC_URL || ''}/pdfjs/pdf.worker.min.mjs`;

/** Longest side of a page thumbnail, in pixels. */
export const THUMBNAIL_LONG_SIDE_PX = 1200;

/** Longest side of a plan rasterised from a PDF page: legible line work. */
export const PLAN_LONG_SIDE_PX = 3000;

type PdfJs = typeof import('pdfjs-dist');

let pdfjsPromise: Promise<PdfJs> | null = null;

const loadPdfJs = (): Promise<PdfJs> => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/legacy/build/pdf.mjs').then(pdfjs => {
      pdfjs.GlobalWorkerOptions.workerSrc = WORKER_SRC;
      return pdfjs;
    });
  }
  return pdfjsPromise;
};

export interface RasterisedPage {
  widthPt: number;
  heightPt: number;
  blob: Blob;
}

const canvasToJpeg = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Could not encode the page image'))), 'image/jpeg', quality);
  });

/**
 * Render the given pages (all, by default) of a PDF to JPEGs whose longest
 * side is `longSidePx`. Sizes are the page's own, in points, with the page's
 * rotation already applied -- what the merge will reproduce.
 *
 * An encrypted PDF is refused with a message the user can act on rather than
 * pdf.js's own exception.
 */
export const rasterisePdf = async (
  file: Blob,
  longSidePx: number,
  pageIndices?: number[],
  quality = 0.8
): Promise<{ pageCount: number; pages: RasterisedPage[] }> => {
  const pdfjs = await loadPdfJs();
  const data = await file.arrayBuffer();
  let doc: PDFDocumentProxy;
  try {
    doc = await pdfjs.getDocument({ data }).promise;
  } catch (err) {
    const name = (err as { name?: string } | null)?.name;
    if (name === 'PasswordException') {
      throw new Error('This PDF is password-protected. Remove the password and try again.');
    }
    if (name === 'InvalidPDFException') {
      throw new Error('This file is not a valid PDF.');
    }
    throw err;
  }

  try {
    const indices = pageIndices ?? Array.from({ length: doc.numPages }, (_, i) => i);
    const pages: RasterisedPage[] = [];
    for (const index of indices) {
      const page = await doc.getPage(index + 1);
      const natural = page.getViewport({ scale: 1 });
      const scale = longSidePx / Math.max(natural.width, natural.height);
      const viewport = page.getViewport({ scale });
      const canvas = window.document.createElement('canvas');
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Could not draw the page');
      // A page with no background of its own is white on paper; the canvas
      // would otherwise be transparent, which JPEG turns black.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: context, viewport }).promise;
      pages.push({
        widthPt: natural.width,
        heightPt: natural.height,
        blob: await canvasToJpeg(canvas, quality),
      });
      page.cleanup();
    }
    return { pageCount: doc.numPages, pages };
  } finally {
    await doc.destroy();
  }
};

/** What an encrypted attachment is refused with, at upload and at export. */
export const ENCRYPTED_PDF_MESSAGE =
  'This PDF has security restrictions (it is encrypted), so its pages cannot be merged into the report. '
  + 'Print it to a new PDF, or save a copy without security, and attach that instead.';

/**
 * Refuse a PDF the export could not merge, before anything is uploaded.
 *
 * pdf.js and pdf-lib disagree about encryption, which is why this check is
 * needed at all. A PDF with only an OWNER password (the "no editing" kind CAD
 * and specification exports carry) opens in pdf.js without a prompt, so the
 * thumbnails come out fine. pdf-lib cannot decrypt, though, and told to
 * ignore the encryption it copies the still-encrypted streams into the report:
 * blank or garbled pages, and no error. The file is read once here with
 * pdf-lib, the library the export uses, which also catches a file pdf-lib
 * cannot parse while the user is still looking at it.
 */
export const assertMergeable = async (file: Blob): Promise<void> => {
  const { PDFDocument } = await import('pdf-lib');
  let isEncrypted: boolean;
  try {
    isEncrypted = (await PDFDocument.load(await file.arrayBuffer(), { ignoreEncryption: true, updateMetadata: false })).isEncrypted;
  } catch (err) {
    console.error('pdf-lib could not read the PDF:', err);
    throw new Error('This PDF could not be read for merging into the report. Print it to a new PDF and attach that instead.');
  }
  if (isEncrypted) throw new Error(ENCRYPTED_PDF_MESSAGE);
};

/**
 * Upload a PDF and its page thumbnails; returns the block data to store.
 * The PDF keeps its extension so a download of it opens as a PDF.
 */
export const uploadPdfAttachment = async (
  supabase: SupabaseClient,
  file: File,
  onProgress?: (message: string) => void
): Promise<Pick<PdfAttachmentBlockData, 'storagePath' | 'filename' | 'pages' | 'title'>> => {
  onProgress?.('Reading pages…');
  await assertMergeable(file);
  const { pages } = await rasterisePdf(file, THUMBNAIL_LONG_SIDE_PX);
  if (pages.length === 0) throw new Error('The PDF has no pages.');

  const id = crypto.randomUUID();
  const storagePath = `${id}.pdf`;

  onProgress?.('Uploading…');
  const { error: pdfError } = await supabase.storage
    .from('document-images')
    .upload(storagePath, file, { contentType: 'application/pdf' });
  if (pdfError) throw new Error(pdfError.message || 'The PDF could not be uploaded');

  const uploaded: AttachmentPage[] = await mapWithConcurrency(pages, 4, async (page, i) => {
    const thumbPath = `${id}-p${i + 1}.jpeg`;
    const { error } = await supabase.storage
      .from('document-images')
      .upload(thumbPath, page.blob, { contentType: 'image/jpeg' });
    if (error) throw new Error(error.message || `Page ${i + 1}'s preview could not be uploaded`);
    return { widthPt: page.widthPt, heightPt: page.heightPt, thumbPath };
  });

  return {
    storagePath,
    filename: file.name,
    title: file.name.replace(/\.pdf$/i, ''),
    pages: uploaded,
  };
};
